import Breadcrumbs from '../components/Breadcrumbs';
import Link from '../components/Link';
import { CONTENT_CLASSES } from '../constants/classNames';
import { ASSISTANT_NAME, CONTACT_EMAIL, PUBLISHER_NAME, PUBLISHER_URL, ROUTES, SITE_NAME } from '../constants/config';
import { LESSON_IDEAS } from '../data/lessonIdeas';
import { getSiteEngine } from '../data/sitePages';
import { WORKSHEETS, findWorksheetBySlug } from '../data/worksheets';
import { buildLabPath, buildWorksheetPath } from '../utils/routeUtils';
import '../styles/ContentPage.css';

function GuideSection({ title, children }) {
  return (
    <section className={CONTENT_CLASSES.section}>
      <h2 className={CONTENT_CLASSES.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

function LessonIdea({ idea }) {
  const worksheet = idea.worksheetSlug ? findWorksheetBySlug(idea.worksheetSlug) : null;
  const engine = getSiteEngine();
  const describePair = (pair) => pair.map((id) => engine.getSubstance(id).name.toLowerCase()).join(' and ');
  return (
    <li className={CONTENT_CLASSES.listItem}>
      <span className={CONTENT_CLASSES.listTitle}>
        {idea.title} (about {idea.minutes} minutes)
      </span>
      <span className={CONTENT_CLASSES.listDetail}>
        <strong>Learning goal:</strong> {idea.goal}
      </span>
      <span className={CONTENT_CLASSES.listDetail}>
        {idea.activity}{' '}
        {idea.pairs?.map((pair) => (
          <Link key={pair.join('+')} to={buildLabPath(pair)}>
            Mix {describePair(pair)} in the lab.{' '}
          </Link>
        ))}
        {worksheet && <Link to={buildWorksheetPath(worksheet)}>Print the {worksheet.title} worksheet.</Link>}
      </span>
    </li>
  );
}

/**
 * A guide for grown-ups: how to use the lab at home and in class, free worksheets, safety and privacy.
 */
export default function ParentsTeachersPage() {
  return (
    <article className={CONTENT_CLASSES.root}>
      <Breadcrumbs
        trail={[
          { label: 'Lab', path: ROUTES.LAB },
          { label: 'Parents & teachers', path: ROUTES.PARENTS },
        ]}
      />
      <header className={CONTENT_CLASSES.hero}>
        <h1 className={CONTENT_CLASSES.heading}>For parents and teachers</h1>
        <p className={CONTENT_CLASSES.lead}>
          {SITE_NAME} is a free virtual chemistry lab where children mix real elements from the periodic table and
          see what happens. {ASSISTANT_NAME}, our cartoon kid scientist, explains every result in simple words. It is
          written for children of about 7 to 12, and older students use it as a quick reference.
        </p>
      </header>

      <GuideSection title="Using the lab at home">
        <p className={CONTENT_CLASSES.text}>
          Children love asking "what happens if...?" The lab lets them find out safely. Sit with your child, let them
          pick two substances, and ask them to guess what will happen before they press Mix. Then listen to{' '}
          {ASSISTANT_NAME}'s explanation together. Asking "were you right?" and "why do you think that happened?" turns
          play into real scientific thinking. Every element has its own page with simple facts, and the{' '}
          <Link to={ROUTES.REACTIONS}>famous reactions</Link> have longer articles with a short quiz.
        </p>
      </GuideSection>

      <GuideSection title="Lesson ideas for the classroom">
        <p className={CONTENT_CLASSES.text}>
          The lab works well on an interactive whiteboard or on tablets in small groups. Each idea below takes 15 to 30
          minutes and needs no equipment beyond a screen.
        </p>
        <ul className={CONTENT_CLASSES.list}>
          {LESSON_IDEAS.map((idea) => (
            <LessonIdea key={idea.title} idea={idea} />
          ))}
        </ul>
      </GuideSection>

      <GuideSection title="Free printable worksheets">
        <p className={CONTENT_CLASSES.text}>
          Each worksheet prints on a single A4 or Letter page in black and white. Use the Print button on the worksheet
          page, or choose "Save as PDF" in your browser's print window to keep a copy. Answer keys are on a separate page.
        </p>
        <ul className={CONTENT_CLASSES.list}>
          {WORKSHEETS.map((worksheet) => (
            <li key={worksheet.slug} className={CONTENT_CLASSES.listItem}>
              <span className={CONTENT_CLASSES.listTitle}>
                <Link to={buildWorksheetPath(worksheet)}>{worksheet.title}</Link>
              </span>
              <span className={CONTENT_CLASSES.listDetail}>
                Ages {worksheet.ages}, about {worksheet.minutes} minutes.
              </span>
            </li>
          ))}
        </ul>
      </GuideSection>

      <GuideSection title="Why the reactions are virtual">
        <p className={CONTENT_CLASSES.text}>
          Many of the reactions in the lab, such as potassium in water or sodium in chlorine, are genuinely dangerous,
          and some involve poisonous or radioactive substances. Showing them virtually lets children see what really
          happens without any risk. Every dangerous reaction carries a safety note, and our "try this at home" ideas only
          use everyday kitchen materials with an adult. Please remind children that real chemicals are only handled by
          trained adults with proper equipment.
        </p>
      </GuideSection>

      <GuideSection title="Privacy and advertising">
        <p className={CONTENT_CLASSES.text}>
          There are no accounts, sign-ups, chats or forms, and we do not collect personal information from children. The
          only thing stored is the "Read to me" setting, kept in the browser on your own device. To keep the lab free we
          may show ads from Google, and every ad request is tagged for child-directed treatment, so no personalised or
          remarketing ads are shown. Read our <Link to={ROUTES.PRIVACY}>privacy policy</Link> for details.
        </p>
      </GuideSection>

      <GuideSection title={`Who makes ${SITE_NAME}`}>
        <p className={CONTENT_CLASSES.text}>
          {SITE_NAME} is a project of <a href={PUBLISHER_URL}>{PUBLISHER_NAME}</a>. We would love to hear how you use it
          and what would help your children or class. Email <strong>{CONTACT_EMAIL}</strong> or visit our{' '}
          <Link to={ROUTES.CONTACT}>contact page</Link>. You can also read more <Link to={ROUTES.ABOUT}>about us</Link>.
        </p>
      </GuideSection>
    </article>
  );
}
